/**
 * 智能导航配置系统
 * 支持一级菜单（职能分组）+ 二级菜单（具体页面）
 * 已优化：移除冗余分组，扁平化为最多两级菜单
 */

import type { CompanyRole, Permission } from '@company/shared'

export interface NavItem {
  id: string
  label: string
  icon: string
  path?: string
  roles?: CompanyRole[]
  permission?: Permission
  children?: NavItem[]
  badgeSource?: BadgeSource
}

export type BadgeSource =
  | 'my-tasks'
  | 'pending-approvals'
  | 'pending-leaves'
  | 'pending-expenses'
  | 'pending-devices'

export const ALL_NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: '仪表盘', icon: 'LayoutDashboard', path: '/dashboard' },

  // 个人中心 - 整合所有"我的"相关入口
  {
    id: 'my',
    label: '个人中心',
    icon: 'User',
    children: [
      { id: 'my-profile', label: '个人资料', icon: 'UserCircle', path: '/settings/profile' },
      {
        id: 'my-tasks',
        label: '我的任务',
        icon: 'ClipboardList',
        path: '/pm/my-tasks',
        badgeSource: 'my-tasks',
      },
      { id: 'my-devices', label: '我的设备', icon: 'Laptop', path: '/my/devices' },
      { id: 'my-payslips', label: '我的工资条', icon: 'Receipt', path: '/hr/salaries/my-payslips' },
      {
        id: 'my-notifications',
        label: '通知偏好',
        icon: 'Bell',
        path: '/settings/my-notifications',
      },
    ],
  },

  // 组织管理 - 新模块，整合组织结构相关功能
  {
    id: 'org',
    label: '组织管理',
    icon: 'Network',
    roles: ['director', 'hr'],
    children: [
      { id: 'organization', label: '组织架构图', icon: 'Network', path: '/organization' },
      { id: 'departments', label: '部门管理', icon: 'Building2', path: '/hr/departments' },
      { id: 'positions', label: '职位管理', icon: 'Briefcase', path: '/hr/positions' },
      { id: 'teams', label: '团队管理', icon: 'UsersRound', path: '/pm/teams' },
    ],
  },

  // 人事管理 - 保留员工、请假、薪资相关
  {
    id: 'hr',
    label: '人事管理',
    icon: 'Users',
    roles: ['director', 'hr'],
    children: [
      { id: 'employees', label: '员工列表', icon: 'Users', path: '/hr/employees' },
      {
        id: 'leaves',
        label: '请假管理',
        icon: 'CalendarOff',
        path: '/hr/leaves',
        badgeSource: 'pending-leaves',
      },
      {
        id: 'leaves-pending',
        label: '待审批请假',
        icon: 'ClipboardCheck',
        path: '/hr/leaves/pending',
      },
      { id: 'leaves-statistics', label: '请假统计', icon: 'PieChart', path: '/hr/leaves/statistics' },
      { id: 'salaries', label: '薪资管理', icon: 'DollarSign', path: '/hr/salaries' },
      { id: 'payslips', label: '工资条', icon: 'FileText', path: '/hr/salaries/payslips' },
    ],
  },

  // 项目管理 - 移除团队列表（已移至组织管理）
  {
    id: 'pm',
    label: '项目管理',
    icon: 'FolderKanban',
    children: [
      {
        id: 'projects',
        label: '项目列表',
        icon: 'FolderKanban',
        path: '/pm/projects',
        permission: 'project.view',
      },
      {
        id: 'issues',
        label: '任务列表',
        icon: 'ListTodo',
        path: '/pm/issues',
        permission: 'issue.view',
      },
      {
        id: 'timelogs',
        label: '工时记录',
        icon: 'Clock',
        path: '/pm/timelogs',
        permission: 'timelog.view',
      },
      {
        id: 'workload-stats',
        label: '工作量统计',
        icon: 'BarChart3',
        path: '/pm/workload-stats',
        roles: ['director', 'pm'],
      },
      {
        id: 'workflow-analytics',
        label: '工作流分析',
        icon: 'TrendingUp',
        path: '/pm/workflow-analytics',
        roles: ['director', 'pm'],
      },

    ],
  },

  // 知识库 - 提升为一级菜单
  {
    id: 'knowledge',
    label: '知识库',
    icon: 'BookOpen',
    children: [
      { id: 'knowledge-base', label: '知识库首页', icon: 'BookOpen', path: '/knowledge-base' },
      { id: 'knowledge-search', label: '知识搜索', icon: 'Search', path: '/knowledge-base/search' },
    ],
  },

  // 财务管理 - 简化层级，直接显示页面
  {
    id: 'finance',
    label: '财务管理',
    icon: 'Wallet',
    roles: ['director', 'finance'],
    children: [
      { id: 'accounts', label: '账户管理', icon: 'Landmark', path: '/finance/accounts' },
      { id: 'cash-flows', label: '现金流', icon: 'TrendingUp', path: '/finance/cash-flows' },
      { id: 'categories', label: '分类管理', icon: 'Tags', path: '/finance/categories' },
      { id: 'transfers', label: '转账记录', icon: 'ArrowLeftRight', path: '/finance/transfers' },
      {
        id: 'reimbursements',
        label: '报销管理',
        icon: 'Receipt',
        path: '/finance/reimbursements',
        badgeSource: 'pending-expenses',
      },
      { id: 'currencies', label: '货币管理', icon: 'Coins', path: '/finance/currencies' },
    ],
  },

  // 行政管理 - 简化层级，直接显示页面
  {
    id: 'admin',
    label: '行政管理',
    icon: 'Building',
    roles: ['director', 'admin'],
    children: [
      {
        id: 'devices',
        label: '设备管理',
        icon: 'Monitor',
        path: '/admin/devices',
        badgeSource: 'pending-devices',
      },
    ],
  },

  // 系统设置
  {
    id: 'settings',
    label: '系统设置',
    icon: 'Settings',
    roles: ['director'],
    children: [
      { id: 'settings-config', label: '系统配置', icon: 'Cog', path: '/settings/config' },
      {
        id: 'settings-notifications',
        label: '通知设置',
        icon: 'BellRing',
        path: '/settings/notifications',
      },
      { id: 'settings-audit', label: '审计日志', icon: 'FileSearch', path: '/settings/audit-logs' },
    ],
  },
]

export const NAVIGATION_ORDER: Record<CompanyRole, string[]> = {
  developer: ['dashboard', 'my', 'pm', 'knowledge'],
  pm: ['dashboard', 'my', 'pm', 'knowledge', 'org'],
  hr: ['dashboard', 'my', 'org', 'hr', 'pm', 'knowledge'],
  finance: ['dashboard', 'my', 'finance', 'pm', 'knowledge'],
  admin: ['dashboard', 'my', 'admin', 'pm', 'knowledge'],
  ops: ['dashboard', 'my', 'pm', 'admin', 'knowledge'],
  director: ['dashboard', 'my', 'org', 'hr', 'pm', 'knowledge', 'finance', 'admin', 'settings'],
}

function filterNavItems(
  items: NavItem[],
  hasPermission: (permission: Permission) => boolean,
  hasRole: (...roles: CompanyRole[]) => boolean
): NavItem[] {
  return items
    .filter((item) => {
      if (item.roles && !hasRole(...item.roles)) {
        return false
      }
      if (item.permission && !hasPermission(item.permission)) {
        return false
      }
      return true
    })
    .map((item) => {
      if (item.children) {
        const filteredChildren = filterNavItems(item.children, hasPermission, hasRole)
        if (filteredChildren.length === 0) {
          return null
        }
        return { ...item, children: filteredChildren }
      }
      return item
    })
    .filter((item): item is NavItem => item !== null)
}

export function getNavigationForRole(
  role: CompanyRole,
  hasPermission: (permission: Permission) => boolean,
  hasRole: (...roles: CompanyRole[]) => boolean
): NavItem[] {
  const roleOrder = NAVIGATION_ORDER[role]
  const filteredItems = filterNavItems(ALL_NAV_ITEMS, hasPermission, hasRole)
  return filteredItems.sort((a, b) => {
    const orderA = roleOrder.indexOf(a.id)
    const orderB = roleOrder.indexOf(b.id)
    return (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB)
  })
}

export const ROLE_PRIMARY_AREAS: Record<CompanyRole, string[]> = {
  developer: ['my-tasks', 'timelogs', 'issues'],
  pm: ['projects', 'issues', 'workload-stats', 'teams'],
  hr: ['employees', 'leaves', 'salaries'],
  finance: ['finance', 'salaries', 'reimbursements'],
  admin: ['devices', 'employees'],
  ops: ['projects', 'issues', 'devices'],
  director: ['projects', 'employees', 'finance', 'devices'],
}

export const SEARCH_ENTITY_PRIORITY: Record<CompanyRole, string[]> = {
  developer: ['issue', 'project', 'timelog', 'employee'],
  pm: ['project', 'issue', 'employee', 'team', 'timelog'],
  hr: ['employee', 'leave', 'salary', 'department', 'position'],
  finance: ['reimbursement', 'cashflow', 'account', 'salary', 'employee'],
  admin: ['device', 'employee'],
  ops: ['project', 'issue', 'device', 'employee'],
  director: ['project', 'employee', 'reimbursement', 'device', 'issue'],
}

export function getSearchPriority(role: CompanyRole, entityType: string): number {
  const priorities = SEARCH_ENTITY_PRIORITY[role]
  const index = priorities.indexOf(entityType)
  return index === -1 ? 999 : index
}
